import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { showAlert } from 'actions/app';
import { setSelectedTemplate } from 'actions/bulkEmail';
import { createFolder, editFolder, getFolderData } from 'services/bulkEmail';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import TemplateDropDown from 'UI/components/molecules/TemplateDropDown';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { TEMPLATE_ENTITY_TYPES } from 'UI/constants/defaults';

import { FORM_FIELDS_MAP } from './fields';
import { useStyles } from './style';

/** @typedef {import('types/app').TemplateFolderDrawerPropTypes} TemplateFolderDrawerPropTypes */

/**
 * @param {TemplateFolderDrawerPropTypes} props
 */
const TemplateFolderDrawer = ({
  templatesTreeItem,
  isFolderBeingEdited = false,
  onClose,
  onSubmitFolder
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false
  });

  const form = useForm({
    defaultValues: isFolderBeingEdited
      ? {
          [FORM_FIELDS_MAP.FolderName.key]: templatesTreeItem.name
        }
      : {
          [FORM_FIELDS_MAP.DestinationParentFolder.key]:
            templatesTreeItem.entity === TEMPLATE_ENTITY_TYPES.Folder
              ? {
                  id: templatesTreeItem.id,
                  name: templatesTreeItem.name
                }
              : templatesTreeItem.parentFolder
        }
  });
  const { register, errors, handleSubmit, setValue, watch } = form;
  const formFields = watch();

  useEffect(() => {
    register({ name: FORM_FIELDS_MAP.FolderName.key }, { required: 'Folder Name is required' });
    register(
      { name: FORM_FIELDS_MAP.DestinationParentFolder.key },
      { required: 'Directory folder is required' }
    );
  }, [register]);

  useEffect(() => {
    isFolderBeingEdited &&
      (async () => {
        setUiState(prev => ({ ...prev, isFormLoading: true }));
        const { success, data, alert } = await getFolderData(templatesTreeItem.id);
        if (success) {
          const parentFolder = { id: data.parentFolder.id, name: data.parentFolder.name };
          dispatch(setSelectedTemplate({ parentFolder }));
          setValue(FORM_FIELDS_MAP.DestinationParentFolder.key, parentFolder);
        } else {
          dispatch(showAlert(alert));
        }
        setUiState(prev => ({ ...prev, isFormLoading: false }));
      })();
  }, [isFolderBeingEdited, templatesTreeItem.id, dispatch, setValue, register]);

  const onSubmit = async ({ folderName, destinationParentFolder }) => {
    setUiState(prevState => ({ ...prevState, isSaving: true }));
    const mappedFormData = {
      name: folderName,
      parentFolderId: destinationParentFolder.id
    };
    const { success, alert, data } = isFolderBeingEdited
      ? await editFolder(templatesTreeItem.id, mappedFormData)
      : await createFolder(mappedFormData);

    if (success) {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false,
        isSuccess: true
      }));

      const newFolderData = {
        name: folderName,
        idsToRefresh: { destinationFolderId: destinationParentFolder.id },
        id: isFolderBeingEdited ? templatesTreeItem.id : data.id
      };
      const parentFolderDidChange =
        isFolderBeingEdited && destinationParentFolder.id !== templatesTreeItem.parentFolder.id;

      if (parentFolderDidChange)
        newFolderData.idsToRefresh.previousFolderId = templatesTreeItem.parentFolder.id;

      onSubmitFolder && onSubmitFolder(newFolderData);
    } else {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false,
        isSuccess: false
      }));
    }

    dispatch(showAlert(alert));
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose,
    isSaveDisabled: uiState.isSaving,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      onClose={onClose}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit), id: 'drawer-form' }}
      CardHeaderProps={{ title: isFolderBeingEdited ? 'Edit Folder' : 'New Folder' }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>
        <Box className={classes.formContainer}>
          <Box className={classes.formInputContainer}>
            <Typography variant="subtitle1">
              {strings.bulkEmails.templateActions.templateFolder.folderName}
            </Typography>
            <TextBox
              name={FORM_FIELDS_MAP.FolderName.key}
              label="Name *"
              width="100%"
              onChange={setValue}
              error={!!errors.folderName}
              errorText={errors.folderName?.message}
              value={formFields.folderName}
            />
          </Box>
          <Box className={classes.formInputContainer}>
            {isFolderBeingEdited ? (
              <>
                <Typography variant="subtitle1">
                  {strings.bulkEmails.templateActions.templateFolder.selectDirectory}
                </Typography>
                <TemplateDropDown
                  folderIdToExclude={templatesTreeItem?.id}
                  formFieldName={FORM_FIELDS_MAP.DestinationParentFolder.key}
                  preselectedFolderId="Mine"
                  placeholder="Choose folder"
                  onlyShowFolders
                  enableIconClick
                />
              </>
            ) : (
              <>
                <Typography variant="subtitle1">
                  {strings.bulkEmails.templateActions.templateFolder.directoryFolder}
                </Typography>
                <TextBox
                  name={FORM_FIELDS_MAP.DestinationParentFolder.key}
                  label="Parent Folder *"
                  width="100%"
                  error={!!errors[FORM_FIELDS_MAP.DestinationParentFolder.key]}
                  errorText={errors[FORM_FIELDS_MAP.DestinationParentFolder.key]?.message}
                  value={formFields[FORM_FIELDS_MAP.DestinationParentFolder.key]?.name}
                  disabled
                />
              </>
            )}
          </Box>
        </Box>
      </FormContext>
    </FPDrawer>
  );
};

export default TemplateFolderDrawer;
